import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Phone, Navigation, Pencil, Sparkles, Award } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageHeader, FullPageSpinner, EmptyState } from "@/components/kit";
import { Stethoscope } from "lucide-react";

export default function VetDashboard() {
  const navigate = useNavigate();
  const [vet, setVet] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [v, a] = await Promise.all([
          apiFetch("/vets/mine"),
          apiFetch("/vets/mine/analytics").catch(() => null),
        ]);
        setVet(v);
        setAnalytics(a);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <FullPageSpinner />;

  if (!vet) {
    return (
      <div className="max-w-lg mx-auto">
        <PageHeader title="Vet dashboard" />
        <div className="rounded-3xl border border-[#E8E3DA] bg-white">
          <EmptyState
            icon={Stethoscope}
            title="Set up your clinic"
            body="Create your listing to appear in Wooflect search and reach pet parents."
            action={
              <button onClick={() => navigate("/app/vet-onboarding")} className="rounded-full bg-[#E86A45] text-white font-extrabold px-6 py-3">
                Set up listing
              </button>
            }
          />
        </div>
      </div>
    );
  }

  const stats = [
    { label: "Profile views", value: analytics?.views_month ?? 0, total: analytics?.views_total ?? 0, icon: Eye },
    { label: "Calls", value: analytics?.calls_month ?? 0, total: analytics?.calls_total ?? 0, icon: Phone },
    { label: "Directions", value: analytics?.directions_month ?? 0, total: analytics?.directions_total ?? 0, icon: Navigation },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title="Vet dashboard"
        right={
          <button data-testid="vet-edit-listing-btn" onClick={() => navigate("/app/vet-onboarding")} className="flex items-center gap-1.5 rounded-full bg-[#F2EFE9] font-bold px-4 py-2.5">
            <Pencil size={16} /> Edit
          </button>
        }
      />

      {/* Listing preview */}
      <div className={`rounded-3xl overflow-hidden border mb-6 ${vet.is_featured ? "border-[#E86A45] bg-[#FFF7F3]" : "border-[#E8E3DA] bg-white"}`} data-testid="vet-listing-preview">
        <div className="flex gap-3 p-3">
          <div className="h-24 w-24 rounded-2xl overflow-hidden bg-[#F2EFE9] shrink-0">
            <img src={vet.photo_url} alt={vet.name} className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0">
            {vet.is_featured && (
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-[#E86A45] text-white">
                <Award size={11} /> Featured
              </span>
            )}
            <p className="font-extrabold truncate mt-1">{vet.name}</p>
            <p className="text-sm text-[#4A4746] truncate">{vet.address}, {vet.city}</p>
            <p className="text-sm text-[#9A938B]">{vet.hours}</p>
          </div>
        </div>
      </div>

      {/* Analytics */}
      <h2 className="font-display text-lg font-extrabold mb-3">This month</h2>
      <div className="grid grid-cols-3 gap-3 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="rounded-3xl bg-white border border-[#E8E3DA] p-4 text-center" data-testid={`vet-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <s.icon size={20} color="#E86A45" className="mx-auto" />
            <p className="font-display text-2xl font-black mt-1">{s.value}</p>
            <p className="text-xs text-[#4A4746]">{s.label}</p>
            <p className="text-[10px] text-[#9A938B] mt-0.5">{s.total} all-time</p>
          </div>
        ))}
      </div>

      {/* Upgrade CTA */}
      {!vet.is_featured && (
        <div className="rounded-3xl bg-[#F6DED5] p-6 text-center">
          <Sparkles size={26} color="#E86A45" className="mx-auto" />
          <h3 className="font-display text-xl font-extrabold mt-2">Get featured</h3>
          <p className="text-[#4A4746] mt-1">Rise to the top of search and reach more pet parents.</p>
          <button
            data-testid="vet-upgrade-btn"
            onClick={() => navigate("/app/subscription")}
            className="mt-4 rounded-full bg-[#E86A45] text-white font-extrabold px-8 py-3 hover:bg-[#d65f3c]"
          >
            View plans
          </button>
        </div>
      )}
    </div>
  );
}
